<script setup lang="ts">
import { Button, Input, ZhPanel } from '@zerohuman/ui'
import { computed } from 'vue'
import type { TaskInputField } from '@/api'
import { typedInputType } from '@/lib/inputFields'
import { type RetryValues, retryFields } from '@/lib/retryCorrection'

/**
 * The retry correction form (#1132, #1337): one field per input a stopped run's task declares, prefilled by the
 * caller from the run's stored input, so a person corrects what is wrong before the retry starts. Run detail and
 * Blockers mount this one form over the one field derivation (`retryFields`), so the two places a retry starts
 * cannot drift into two input schemas. The caller sends the correction on `submit`; the form shows the server's
 * field-level refusal against the field it names and keeps every value typed.
 */
const props = defineProps<{
    /** The fields the run's task declares; a `file` field is left out by `retryFields`. */
    inputs: TaskInputField[] | null | undefined
    /** A field-level refusal per field key, as the server worded it. */
    errors: Record<string, string>
    busy: boolean
}>()
const values = defineModel<RetryValues>({ required: true })
const emit = defineEmits<{ submit: []; close: [] }>()

const fields = computed(() => retryFields(props.inputs))

/** A text or number field's value as the kit `Input` holds it: the form keeps those kinds as the text typed. */
function retryText(key: string): string {
    const value = values.value[key]
    return typeof value === 'string' ? value : ''
}

function setRetryText(key: string, value: string | number): void {
    values.value[key] = String(value)
}
</script>

<template>
	<ZhPanel as="form" measure="form" data-testid="retry-form" class="space-y-3" @submit.prevent="emit('submit')">
		<p class="text-muted-foreground text-sm">
			<slot>Starts a new run with these inputs. This run stays as it was.</slot>
		</p>
		<label v-for="field in fields" :key="field.key" class="block text-sm">
			<span class="text-muted-foreground">{{ field.label }}{{ field.required ? ' (required)' : '' }}</span>
			<input
				v-if="field.type === 'boolean'"
				v-model="values[field.key]"
				:data-testid="`retry-input-${field.key}`"
				class="mt-1 block"
				type="checkbox"
			/>
			<Input
				v-else
				:model-value="retryText(field.key)"
				:data-testid="`retry-input-${field.key}`"
				class="mt-1"
				:placeholder="field.placeholder"
				:type="typedInputType(field)"
				@update:model-value="value => setRetryText(field.key, value)"
			/>
			<span v-if="errors[field.key]" :data-testid="`retry-error-${field.key}`" class="text-destructive mt-1 block">
				{{ errors[field.key] }}
			</span>
		</label>
		<div class="flex gap-2">
			<Button type="button" size="sm" data-testid="retry-submit" :disabled="busy" @click="emit('submit')">
				Start new run
			</Button>
			<Button type="button" size="sm" variant="ghost" :disabled="busy" @click="emit('close')">Close</Button>
		</div>
	</ZhPanel>
</template>
